import type {
  CustomerBillingAccessState,
  CustomerBillingStatus,
  CustomerRegistrationMode,
} from '../services/registrations/customers/types';
import type { PlanSignupIntent } from './planCheckout';
import { buildAssinarPath, isBillingEnforcementEnabled } from './planCheckout';
import { canUseApp, mapSnapshotToBillingContext } from './customerBillingAccess';
import type { CustomerSubscriptionSnapshot } from './customerSubscription';

export interface PostAuthSubscriptionContext {
  registrationMode?: CustomerRegistrationMode | null;
  billingStatus?: CustomerBillingStatus | null;
  accessState?: CustomerBillingAccessState | null;
  accessBlockedAt?: string | null;
  pastDueAt?: string | null;
  trialDaysRemaining?: number | null;
  planSlug?: string | null;
}

const toBillingContext = (
  subscription?: PostAuthSubscriptionContext | null
): ReturnType<typeof mapSnapshotToBillingContext> | null => {
  if (!subscription) return null;

  return mapSnapshotToBillingContext({
    registrationMode: subscription.registrationMode ?? null,
    billingStatus: subscription.billingStatus ?? null,
    accessState: subscription.accessState ?? null,
    accessBlockedAt: subscription.accessBlockedAt ?? null,
    pastDueAt: subscription.pastDueAt ?? null,
    billingPreference: null,
    chargedPriceCents: null,
    commitmentEndsAt: null,
    commitmentDaysRemaining: null,
    trialDaysRemaining: subscription.trialDaysRemaining ?? null,
    planSlug: subscription.planSlug ?? null,
    planName: null,
    isAiActivated: false,
  });
};

const resolveRegistrationMode = (
  subscription?: PostAuthSubscriptionContext | null,
  intent?: PlanSignupIntent | null
): CustomerRegistrationMode | null => {
  if (subscription?.registrationMode) return subscription.registrationMode;
  if (intent?.mode) return intent.mode;
  return null;
};

export const resolvePostAuthPath = ({
  isAdmin,
  isConsultant,
  intent,
  subscription,
}: {
  isAdmin: boolean;
  isConsultant?: boolean;
  intent?: PlanSignupIntent | null;
  subscription?: PostAuthSubscriptionContext | null;
}): string => {
  if (isAdmin) return '/admin';
  if (isConsultant) return '/consultant/dashboard';

  const registrationMode = resolveRegistrationMode(subscription, intent);
  const planSlug = subscription?.planSlug ?? intent?.planSlug ?? null;
  const billingContext = toBillingContext(subscription);
  const hasAccess = billingContext ? canUseApp(billingContext) : false;

  if (isBillingEnforcementEnabled()) {
    if (registrationMode === 'subscribe' && !hasAccess && planSlug) {
      return buildAssinarPath(planSlug, 'subscribe');
    }

    if (intent?.mode === 'subscribe' && planSlug && !hasAccess) {
      return buildAssinarPath(planSlug, 'subscribe');
    }

    if (subscription?.accessState === 'blocked' && planSlug) {
      return buildAssinarPath(planSlug, 'subscribe');
    }
  }

  return '/dashboard/summary';
};

export const requiresSubscriptionPayment = (
  subscription?: PostAuthSubscriptionContext | null,
  options?: { isConsultant?: boolean }
): boolean => {
  if (!isBillingEnforcementEnabled()) return false;
  if (options?.isConsultant) return false;
  if (!subscription) return false;

  if (subscription.accessState === 'blocked') return true;

  const billingContext = toBillingContext(subscription);
  if (!billingContext) return false;

  if (subscription.registrationMode !== 'subscribe') return false;
  return !canUseApp(billingContext);
};

export const snapshotToPostAuthContext = (
  snapshot: CustomerSubscriptionSnapshot
): PostAuthSubscriptionContext => ({
  registrationMode: snapshot.registrationMode,
  billingStatus: snapshot.billingStatus,
  accessState: snapshot.accessState,
  accessBlockedAt: snapshot.accessBlockedAt,
  pastDueAt: snapshot.pastDueAt,
  trialDaysRemaining: snapshot.trialDaysRemaining,
  planSlug: snapshot.planSlug,
});

export const SUBSCRIPTION_PAYMENT_GATE_WHITELIST = ['/assinar', '/my-account'] as const;

export const isSubscriptionPaymentGateWhitelisted = (pathname: string): boolean =>
  SUBSCRIPTION_PAYMENT_GATE_WHITELIST.some(
    (allowed) => pathname === allowed || pathname.startsWith(`${allowed}/`)
  );
